import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { DeviceConfigurationDtoIncoming, DeviceConfigurationDtoOutbound, DisplayPortRequest, DisplayPortResponse } from '../models/device-configuration.model';
import { ApiResponse, PageableApiResponse } from '../../api-response';
import { DeviceConfigurationSummary } from '../../provisioned-devices/models/device-provisioning-request.model';
import { BaseApiService } from '../../../core/base-api.service';

@Injectable({
  providedIn: 'root'
})
export class DeviceConfigurationService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly deviceConfigurationBaseUrl = `${this.coreApiUrl}/api/v1/device-configuration`;

  getDeviceConfigurationById(id: number): Observable<ApiResponse<DeviceConfigurationDtoIncoming>> {
    return this.http.get<ApiResponse<DeviceConfigurationDtoIncoming>>(`${this.deviceConfigurationBaseUrl}/${id}`);
  }

  getDeviceConfigurationsPaginated(page: number, pageSize: number, orderColumn: string, orderDirection: string): Observable<PageableApiResponse<DeviceConfigurationDtoIncoming>> {
    return this.http.get<PageableApiResponse<DeviceConfigurationDtoIncoming>>(
      `${this.deviceConfigurationBaseUrl}?page=${page}&size=${pageSize}&sort=${orderColumn},${orderDirection}`
    );
  }

  postDeviceConfiguration(deviceConfiguration: DeviceConfigurationDtoOutbound): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(this.deviceConfigurationBaseUrl, deviceConfiguration);
  }

  putDeviceConfiguration(id: number, deviceConfiguration: DeviceConfigurationDtoOutbound): Observable<ApiResponse<null>> {
    return this.http.put<ApiResponse<null>>(`${this.deviceConfigurationBaseUrl}/${id}`, deviceConfiguration);
  }

  // Display Port CRUD
  getDisplayPortsForConfiguration(configId: number): Observable<ApiResponse<DisplayPortResponse[]>> {
    return this.http.get<ApiResponse<DisplayPortResponse[]>>(
      `${this.coreApiUrl}/api/v1/display-port/for-device-configuration/${configId}`
    );
  }

  addDisplayPort(configId: number, port: DisplayPortRequest): Observable<ApiResponse<DisplayPortResponse>> {
    return this.http.post<ApiResponse<DisplayPortResponse>>(
      `${this.coreApiUrl}/api/v1/display-port/for-device-configuration/${configId}`,
      port
    );
  }

  updateDisplayPort(portId: number, port: DisplayPortRequest): Observable<ApiResponse<DisplayPortResponse>> {
    return this.http.put<ApiResponse<DisplayPortResponse>>(
      `${this.coreApiUrl}/api/v1/display-port/${portId}`,
      port
    );
  }

  deleteDisplayPort(portId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.coreApiUrl}/api/v1/display-port/${portId}`
    );
  }

  getListForDeviceConfigurationAssignmentUsage(): Observable<DeviceConfigurationSummary[]> {
    return this.http.get<ApiResponse<DeviceConfigurationSummary[]>>(`${this.deviceConfigurationBaseUrl}/list-for-assignment`).pipe(
      map(response => response.data)
    );
  }
}
